/**
 * Permission prompt modal.
 *
 * Renders the head of `usePermissionStore.pending` whenever a tool call needs
 * user approval (only fires for agent hosts in "ask" mode). Sends the user's
 * decision back through `sendPermissionResponse` and dequeues.
 *
 * Keyboard:
 *   - Enter      → Approve once
 *   - Shift+Enter→ Approve for this turn
 *   - Esc        → Deny
 *
 * Auto-deny safety net: 5-minute countdown. If the user walks away from the
 * tab the prompt resolves itself with `deny` so the host's canUseTool callback
 * doesn't hang forever.
 */

import { useEffect, useMemo, useState } from "react";
import { ShieldAlert } from "lucide-react";
import type {
  AgentPermissionRequest,
  AgentPermissionResponse,
  AgentRole,
} from "@paperclip-cloud/shared";
import { Modal } from "./Modal";
import { usePermissionStore } from "../stores/permissions";
import { sendPermissionResponse } from "../api/ws";

const AUTO_DENY_MS = 5 * 60 * 1000;

const ROLE_LABEL: Record<AgentRole, string> = {
  ceo: "CEO",
  ops: "Operations",
};

const RISK_TONE: Record<
  AgentPermissionRequest["risk"],
  { className: string; label: string }
> = {
  low: { className: "bg-status-running/15 text-status-running", label: "low risk" },
  medium: {
    className: "bg-status-blocked/15 text-status-blocked",
    label: "medium risk",
  },
  high: { className: "bg-status-error/15 text-status-error", label: "high risk" },
};

function formatJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export function PermissionPromptModal() {
  const pending = usePermissionStore((s) => s.pending);
  const resolve = usePermissionStore((s) => s.resolve);
  const head = pending[0] ?? null;

  // Reset countdown whenever the head request changes.
  const [remainingMs, setRemainingMs] = useState(AUTO_DENY_MS);

  useEffect(() => {
    if (!head) return;
    setRemainingMs(AUTO_DENY_MS);
    const start = Date.now();
    const tick = window.setInterval(() => {
      const left = AUTO_DENY_MS - (Date.now() - start);
      if (left <= 0) {
        window.clearInterval(tick);
        decide(head, "deny");
      } else {
        setRemainingMs(left);
      }
    }, 250);
    return () => window.clearInterval(tick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [head?.request_id]);

  const decide = (
    req: AgentPermissionRequest,
    decision: AgentPermissionResponse["decision"],
  ) => {
    sendPermissionResponse({
      company_id: req.company_id,
      request_id: req.request_id,
      decision,
    });
    resolve(req.request_id);
  };

  // Keyboard shortcuts. Bound only while a request is active.
  useEffect(() => {
    if (!head) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        decide(head, e.shiftKey ? "allow_session" : "allow_once");
      } else if (e.key === "Escape") {
        e.preventDefault();
        decide(head, "deny");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [head?.request_id]);

  const argsText = useMemo(() => formatJson(head?.args), [head]);

  if (!head) return null;

  const risk = RISK_TONE[head.risk];
  const seconds = Math.ceil(remainingMs / 1000);
  const mm = Math.floor(seconds / 60);
  const ss = String(seconds % 60).padStart(2, "0");

  return (
    <Modal
      open
      onClose={() => decide(head, "deny")}
      title={`${ROLE_LABEL[head.role]} wants to run a tool`}
      size="md"
      footer={
        <>
          <button
            type="button"
            className="btn btn--ghost btn--sm text-status-error"
            onClick={() => decide(head, "deny")}
          >
            Deny (Esc)
          </button>
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={() => decide(head, "allow_session")}
          >
            Approve for this turn (Shift+Enter)
          </button>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => decide(head, "allow_once")}
          >
            Approve once (Enter)
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <ShieldAlert size={14} className="text-status-blocked" aria-hidden="true" />
          <span className="badge badge--accent">{ROLE_LABEL[head.role]}</span>
          <span className={`badge ${risk.className}`}>{risk.label}</span>
          <span className="ml-auto micro" aria-live="polite">
            auto-deny in {mm}:{ss}
          </span>
        </div>

        <div>
          <div className="micro mb-1">tool</div>
          <div className="font-mono text-[13px] text-primary">{head.tool}</div>
        </div>

        <div>
          <div className="micro mb-1">args</div>
          <pre className="bg-sunken text-secondary border border-border-subtle rounded-sm p-2 font-mono text-[11px] leading-[16px] whitespace-pre-wrap break-all max-h-[40vh] overflow-auto">
            {argsText}
          </pre>
        </div>

        {pending.length > 1 && (
          <div className="text-[11px] text-tertiary">
            +{pending.length - 1} more pending after this one.
          </div>
        )}
      </div>
    </Modal>
  );
}
